import React, { useEffect } from "react";
import fp from "lodash/fp";
import { useFormContext } from "react-hook-form";

import { TextWithLoader, HookFormInputGroup } from "components";
import { FormColumn, FormRow } from "components/layout";
import { getEntityTypeName } from "mappers";
import { PartnerTypes, SplitInformationProps } from "types";
import { floatWithComma2Normalizer } from "validators";
import { maxValue100, onlyPercent, required } from "validators/validation";

import { WaterfallExampleMessage } from "./waterfallExampleMessage";

export const PrimarySplitInformationFields = ({
	name,
	isLoading = false,
	splitInfo,
	currentAgent,
	isEditing,
	isStraightSplit,
	isWaterfall,
}: SplitInformationProps) => {
	const {
		setValue,
		getValues,
		watch,
		register,
		formState: { errors },
	} = useFormContext();

	useEffect(() => {
		if (splitInfo?.agent) {
			let agent = {
				id: splitInfo.agent.id,
				name: splitInfo.agent.name,
				entityType: splitInfo.agent.entityType,
				partnerType: splitInfo.agent.partnerType,
				partnerTypeDisplayName: splitInfo.agent.partnerTypeDisplayName,
			};
			setValue(`${name}.agent`, agent);
		}
	}, [splitInfo?.agentId, splitInfo?.agent, name, setValue]);

	const agent = watch(`${name}.agent`);
	const totalRevenueShare = watch("totalRevenueShare");

	const isAgentSelected = fp.isEmpty(agent);

	useEffect(() => {
		setValue(`${name}.schedule_a_id`, null);
	}, [agent?.id, setValue, name]);

	useEffect(() => {
		if (!isEditing) setValue(`${name}.agent`, currentAgent);
	}, [currentAgent, isEditing, name, setValue]);

	const payoutAmountBeforeAdjustmentsValue = getValues(
		`${name}.calculation.payoutAmountBeforeAdjustments`,
	);

	const exampleValue = floatWithComma2Normalizer(
		payoutAmountBeforeAdjustmentsValue,
	);

	return (
		<>
			<FormRow>
				<FormColumn data-autotest="agent">
					<TextWithLoader
						label="Agent"
						value={agent?.name}
						loading={isLoading}
						labelCol={6}
						valueCol={6}
					/>
				</FormColumn>
				<FormColumn data-autotest="residualsSplit">
					<div className={isStraightSplit ? "d-block" : "d-none"}>
						<TextWithLoader
							label="Residuals Split, %"
							value={totalRevenueShare}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					</div>
					<HookFormInputGroup
						register={register}
						name={`${name}.splitPercent`}
						label="Residuals Split, %"
						placeholder="0"
						colLabel={6}
						colInput={6}
						validator={{
							required,
							maxValue100,
						}}
						normalize={onlyPercent}
						errors={errors}
						blurLoading={isLoading}
						showRequiredMark={!isStraightSplit}
						defaultValue={splitInfo?.splitPercent}
						readOnly={isStraightSplit}
						className={isStraightSplit ? "d-none" : "d-flex"}
						external={
							isWaterfall && (
								<WaterfallExampleMessage value={exampleValue} />
							)
						}
					/>
				</FormColumn>
			</FormRow>
			<FormRow className="mt-3">
				<FormColumn data-autotest="entityType">
					{!isAgentSelected && (
						<TextWithLoader
							label="Entity Type"
							value={getEntityTypeName(agent?.entityType)}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					)}
				</FormColumn>
			</FormRow>
			<FormRow className="mt-3">
				<FormColumn data-autotest="partnerType">
					{!isAgentSelected && (
						<TextWithLoader
							label="Partner Type"
							value={
								PartnerTypes.getByValue(agent?.partnerType)
									?.displayName || ""
							}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					)}
				</FormColumn>
			</FormRow>
		</>
	);
};
